import React, { useCallback, useContext, useEffect, useState } from 'react';
import { LoadingIndicator, LoadingSkeleton } from '#lib/components/loading.jsx';
import { useUsers } from './use-users.jsx';
import useUserQueries from '#hooks/use-user-query.js';
import { Detail } from './detail.jsx';
import toast from 'react-hot-toast';
import { UserButtons } from './user-buttons.jsx';
import useDetail from './use-detail.js';
import useBlockNavigation from '#hooks/use-block-navigation.js';
import UnsavedChangesModal from '#components/unsaved-changes-modal.jsx';
import ManageControls from '#components/manage-controls.jsx';
import UsersList from './user-list.jsx';
import { useNavigate } from 'react-router-dom';
import useDebounce from '#lib/hooks/use-debounce.js';
import { UserContext } from '#src/index.jsx';

// USERS COMPONENT
function Users() {
    // STATES
    const [errors, setErrors] = useState({});
    const [query, setQuery] = useState(''); // stringa barra di ricerca
    const [selectedUserId, setSelectedUserId] = React.useState(null); // id utente selezionato
    const [availableUsers, setAvailableUsers] = React.useState(0); // numero di utenti disponibili rimanenti

    const { getLanguage } = useContext(UserContext);
    const lang = getLanguage();

    // QUERIES
    const search = useDebounce(query, 300); // stringa cercata viene aggiornata ogni 0.3s con la stringa nella barra di ricerca

    const {
        userData,
        usersList,
        availableUsers: availableUsersData,
        totalUsers,
        refetch: queryUsers,
        isLoading,
        createUser,
        deleteUser,
        getUser,
        resetUserPassword: resetPassword,
        updateUser
    } = useUserQueries(search, selectedUserId, lang);

    const isSaving = updateUser.isLoading || createUser.isLoading;
    // users reducer
    const userProps = useUsers(usersList, selectedUserId, setSelectedUserId);
    const { users, addUser, removeUser, editUser, resetUser } = userProps;

    const getSelectedUser = useCallback(
        (id) => {
            return Array.isArray(users) && users.find((el) => el.id === selectedUserId);
        },
        [users, users?.length, selectedUserId]
    );
    ////////////////////////////////

    ////////////////////////////////
    // HOOKS
    // block navigation
    const navigate = useNavigate();
    const { proceedNavigation, blocked, reset, setUnsavedChanges } = useBlockNavigation();

    // detail
    const { handleChange, handleChangeCheckbox, imageProfile, imageProfileData } = useDetail({
        selectedUser: getSelectedUser(selectedUserId),
        editUser,
        errors,
        setUnsavedChanges
    });
    ////////////////////////////////6

    ////////////////////////////////
    // HANDLERS

    const handleAddUser = () => {
        const user = users.find((el) => el.id === selectedUserId);
        let confirm;
        if (user?.isEditing) confirm = window.confirm(_t.global.saveReminder());
        if (confirm === false) return;

        if (!availableUsers) return toast.error(_t.users.noUsersAvailable());
        addUser();
        // se confermi vuol dire che elimini un utente e quindi -1
        setUnsavedChanges(true);
        if (confirm) {
            const dbUser = usersList.find((el) => el.id === selectedUserId);
            if (dbUser) resetUser(selectedUserId, dbUser);
            else removeUser(selectedUserId);
            setUnsavedChanges(false);
            setErrors(null);
        }
    };

    const handleDeleteUser = (user) => {
        userProps && removeUser(user.id);
        // se è selezionato tolgo flag modifiche non salvate
        if (selectedUserId === user.id) {
            setUnsavedChanges(() => false);
            setErrors(null);
        }
        // se è su db lo cancello da db
        if (!user.isNew) deleteUser.mutate(user.id);
        if (availableUsers != -1) setAvailableUsers((prev) => prev + 1);
        // if (user.id === selectedUser?.id) {
        if (user.id === selectedUserId) {
            setSelectedUserId(null);
        }
    };

    const handleResetPassword = (password) => {
        resetPassword.mutate({ id: selectedUserId, password, lang });
    };

    const handleSaveUser = (user) => {
        if (users.some((el) => el.id !== user.id && el.id.startsWith('NEW'))) {
            const confirm = window.confirm(_t.global.saveReminder());
            if (!confirm) return;
        }

        // selezione update o create per la mutazione
        let updateFn;
        if (user.isNew) updateFn = createUser;
        else updateFn = updateUser;

        user.isEditing = false;
        const userModel = structuredClone(user);
        delete userModel.isNew;
        delete userModel.isEditing;
        setUnsavedChanges(false);
        setErrors(null);
        updateFn.mutate({ ...userModel, lang });
    };

    const handleSelectUser = (id, index) => {
        if (selectedUserId === id) return;

        if (!selectedUserId) {
            setSelectedUserId(id);
            return;
        }
        const user = getSelectedUser(selectedUserId);
        // NON MODIFICATO || CONFERMO => SELEZIONO
        if (!(user && user.isEditing) || window.confirm(_t.global.saveReminder())) {
            if (user) {
                const dbUser = usersList.find((el) => el.id === user.id);
                // SE PROFILO SELEZIONATO => SCARTO I CHANGES
                if (dbUser) resetUser(user.id, dbUser);
                else removeUser(user.id);
            }
            setSelectedUserId(id);
        }
        setUnsavedChanges(false);
        setErrors(null);
    };

    ///////////////////////////////
    // EFFECTS
    useEffect(() => {
        const user = getSelectedUser(selectedUserId);
        if (user) {
            !user.isNew && getUser.mutate(selectedUserId);
        }
    }, [selectedUserId]);

    useEffect(() => {
        if (userData && !getUser.isLoading) {
            resetUser(userData?.id, userData);
        }
    }, [userData]);

    useEffect(() => {
        const newUser = users?.find((el) => el.isNew);
        if (newUser) setSelectedUserId(newUser.id);
    }, [users]);

    useEffect(() => {
        setAvailableUsers(availableUsersData);
    }, [availableUsersData]);
    ///////////////////////////////

    ///////////////////////////////
    // NAVIGATE
    const goToBilling = () => {
        navigate('/settings/billing');
    };

    const goToAccount = () => {
        navigate('/settings/account');
    };
    ///////////////////////////////

    if (isLoading)
        return (
            <LoadingSkeleton className="relative w-full h-full flex items-start justify-center">
                <LoadingIndicator className="h-1/2" />
            </LoadingSkeleton>
        );

    // const activeUser = selectedUserIndex ? users[selectedUserIndex] : users.find((el) => el.id === selectedUser?.id);
    const selectedUser = getSelectedUser(selectedUserId);
    const hideButtons = !selectedUser || selectedUser?.main;

    return (
        <div className="px-1 flex flex-col lg:flex-row justify-between gap-y-2 md:gap-x-4 w-full h-full">
            <div className="fixed w-full h-[116px] bg-white top-0 left-0 z-50 shadow-sm">
                <ManageControls.BarSlot className="w-auto">
                    <p className="text-2xl"> {`${_t.users.users()}`}</p>
                </ManageControls.BarSlot>
                <UserButtons
                    users={users}
                    selectedUser={selectedUser}
                    handleSaveUser={handleSaveUser}
                    handleDeleteUser={handleDeleteUser}
                    isLoading={isSaving}
                    setErrors={setErrors}
                    disabled={hideButtons}
                />
            </div>
            <section className="w-full h-full pt-[117px] pb-1 md:flex md:flex-row">
                <UsersList
                    selectedUserId={selectedUser?.id}
                    query={query}
                    onAdd={handleAddUser}
                    setQuery={setQuery}
                    onDelete={handleDeleteUser}
                    onQuery={queryUsers}
                    onSelect={handleSelectUser}
                    goToBilling={goToBilling}
                    availableUsers={availableUsers}
                    totalUsers={totalUsers}
                    {...userProps}
                />
                <Detail
                    {...userProps}
                    key={selectedUser?.id}
                    errors={errors}
                    selectedUser={selectedUser}
                    handleChange={handleChange}
                    handleChangeCheckbox={handleChangeCheckbox}
                    handleResetPassword={handleResetPassword}
                    imageProfileData={imageProfileData}
                    goToAccount={goToAccount}
                    isLoading={getUser.isLoading}
                />
                <UnsavedChangesModal isOpen={blocked} onClose={reset} onConfirm={proceedNavigation} />
            </section>
        </div>
    );
}

Users.defaultProps = {
    dropDown: false
};
export default Users;
